import { Fragment, ReactNode, useEffect, useMemo, useState } from 'react';
import { Country, InputRuleApiModel, MarketplaceOrderApiModel } from '~/lib/api/smartsend/types';
import { SumNumber } from '../../lib/helpers/ArrayHelper';
import Loading from '../../../../components/loading/Loading';
import { ContentsSelectionRow } from './ContentsSelectionRow';
import { DimensionAndWeightDisplay } from './DimensionDisplay';
import { GetCollatedCustomerReferenceNumbers } from './item/OrderReferenceNumberUtils';
import { P2GBox, P2GGrid, P2GDivider, P2GText, P2GButton } from '@parcel2go/core-ui';
import { AppCard } from '~/components/card/app-card';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

interface props {
  onSave?: (value: number, items: MarketplaceOrderApiModel[]) => void;
  onUpdate?: (value: number, items: MarketplaceOrderApiModel[]) => void;
  close?: ReactNode;
  items: MarketplaceOrderApiModel[];
  countries: () => Promise<Country[]>;
  getValidation: () => Promise<{ ProductValidation: InputRuleApiModel[] }>;
  tariffSystem: string;
  totalWeight: number;
  currencyCode: string;
}

export const ContentsSelection: React.FC<props> = (props) => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [items, setItems] = useState<MarketplaceOrderApiModel[]>(props.items);
  const [total, setTotal] = useState(0);
  const [validation, setValidation] = useState<{ ProductValidation?: InputRuleApiModel[] }>();
  const hasMultipleOrderNumbers = useMemo(
    () => GetCollatedCustomerReferenceNumbers(props.items).length > 1,
    [props.items]
  );
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  useEffect(() => {
    props.countries().then(setCountries);
    props.getValidation().then((x) => setValidation(x || { ProductValidation: [] }));
  }, []);

  useEffect(() => {
    if (props.onUpdate) {
      props.onUpdate(SumNumber(items.map((x) => x.Value)), items);
    }
    setTotal(SumNumber(items.map((x) => x.Value)) || 0);
  }, [items]);

  const update = (index: number, item: MarketplaceOrderApiModel) => {
    const updated = [...items];
    Object.assign(updated[index], item);
    setItems(updated);
    setTotal(SumNumber(updated.map((x) => x.Value)) || 0);
  };

  return (
    <P2GBox>
      <AppCard>
        <AppCard>
          <P2GBox p={2}>
            <P2GGrid container direction="column" spacing={2}>
              {props.items.map((item, index) => (
                <Fragment key={`${index}_${item.Id}`}>
                  {index > 0 && (
                    <P2GGrid item>
                      <P2GDivider />
                    </P2GGrid>
                  )}
                  <P2GGrid item>
                    {validation ? (
                      <ContentsSelectionRow
                        productValidation={validation.ProductValidation || []}
                        marketplaceOrder={item}
                        onUpdate={(updated) => update(index, updated)}
                        countries={countries}
                        tariffSystem={props.tariffSystem}
                        showReferenceNumber={hasMultipleOrderNumbers}
                        currencyCode={props.currencyCode}
                      />
                    ) : (
                      <Loading />
                    )}
                  </P2GGrid>
                </Fragment>
              ))}
            </P2GGrid>
          </P2GBox>
        </AppCard>
        <P2GBox p={2}>
          <P2GGrid container>
            <P2GGrid item xs={6}>
              <P2GText>
                <Trans
                  t={t}
                  i18nKey="CONTENTS_TOTAL_VALUE"
                  values={{ value: toDisplayCurrency(props.currencyCode, total) }}
                  defaults="<strong>Shipment Value: {{value}}</strong>"
                />
              </P2GText>
            </P2GGrid>
            <P2GGrid item xs={6}>
              <P2GText>
                <Trans
                  t={t}
                  i18nKey="CONTENTS_TOTAL_WEIGHT"
                  components={{
                    dimensionComponent: <DimensionAndWeightDisplay dimensions={{ Weight: props.totalWeight }} />
                  }}
                  defaults="<strong>Shipment Weight:  <dimensionComponent /></strong>"
                />
              </P2GText>
            </P2GGrid>
          </P2GGrid>
        </P2GBox>
      </AppCard>
      {props.onSave && (
        <P2GBox mt={2} textAlign="center">
          <P2GButton onClick={() => {}}>{t('SAVE_BUTTON', { defaultValue: 'Save' })}</P2GButton>
        </P2GBox>
      )}
    </P2GBox>
  );
};
